import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Order } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { createOrdersApi, orderKeys } from '../api';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Cancels an order and invalidates its detail, list, tasks, and history queries.
 * Cancel always writes a status history entry, visible in both the mixed History feed
 * and the lifecycle timeline.
 */
export function useCancelOrder(orderId: string): UseMutationResult<Order, Error, void> {
  const client = useApiClient('deliveries');
  const api = createOrdersApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('orders');

  return useMutation<Order, Error, void>({
    mutationFn: () => api.cancelOrder(orderId),
    ...createMutationCallbacks<Order, void>({
      errorKey: 'drawer.actions.cancel.toast.error',
      invalidateKeys: [
        orderKeys.detail(orderId),
        orderKeys.lists(),
        orderKeys.tasks(orderId),
        orderKeys.history(orderId),
        orderKeys.statusHistory(orderId),
      ],
      queryClient,
      successKey: 'drawer.actions.cancel.toast.success',
      t,
    }),
  });
}
